import { Link, useNavigate } from 'react-router';
import { useState } from 'react';
import { OnboardingLauncher } from '../../onboarding/OnboardingLauncher';
import { useAppConfig } from '../../app/configContext';
import { PRODUCT_NAME } from '../../app/product';
import { HOME_PATH, goToServer } from '../../app/server';
import { testId } from '../../app/testId';
import { usePageTitle } from '../../app/usePageTitle';
import { Button, Icon, LinkButton } from '../../ui';
import { TrialPromise } from '../acquisition/AcquisitionLayout';
import { BrandLogo } from '../../app/brand/BrandLogo';
import { WELCOME_COPY } from './copy';
import { PerformancePreview } from '../acquisition/PerformancePreview';
import { FeatureExplorer } from './FeatureExplorer';
import styles from './WelcomePage.module.css';

// T-511: trial signup is primary. Existing demo and local workspace entry points stay available.
export { HOME_PATH } from '../../app/server';

/**
 * "Try the demo" — a link on a demo build, the server switch on an API build (T-210). PRIMARY on a build with
 * no account configured, SECONDARY on a build that has one (T-432): the words and the id never move.
 */
function TryTheDemo({ variant }: { variant: 'primary' | 'secondary' }) {
  const { apiBuild } = useAppConfig();
  const words = (
    <>
      {WELCOME_COPY.tryTheDemo}
      <Icon name="arrow-right" />
    </>
  );
  return apiBuild ? (
    <Button
      variant={variant}
      size="lg"
      className={styles.ctaButton}
      testID={testId('welcome', 'try-demo')}
      onClick={() => goToServer('demo', HOME_PATH)}
    >
      {words}
    </Button>
  ) : (
    <LinkButton
      to={HOME_PATH}
      variant={variant}
      size="lg"
      className={styles.ctaButton}
      testID={testId('welcome', 'try-demo')}
    >
      {words}
    </LinkButton>
  );
}

/**
 * The hero's calls to action (REQ-002 AC1, v2.9).
 *
 * `apiBuild` is the whole condition and it is the right one: it is the BUILD's own answer to "is there an
 * account configured?" (`VITE_API_MODE=api` plus its base URL), it cannot be turned on by an address or by
 * storage (`app/config.ts`), and it stays true while such a build is showing the in-memory demo — which is
 * exactly the state a reader is in when they want the way back.
 */
function WelcomeActions() {
  const { apiBuild } = useAppConfig();
  return (
    <div className={styles.ctaPair}>
      <LinkButton
        to="/signup"
        variant="primary"
        size="lg"
        className={styles.ctaButton}
        testID="welcome.start-trial"
      >
        Start my free 90 days <Icon name="arrow-right" />
      </LinkButton>
      <TrialPromise />
      <TryTheDemo variant="secondary" />
      {apiBuild && (
        <>
          <Button
            variant="ghost"
            size="sm"
            className={styles.ctaButton}
            testID={testId('welcome', 'open-live')}
            aria-describedby="welcome-open-live-note"
            onClick={() => goToServer('api', HOME_PATH)}
          >
            {WELCOME_COPY.openMyAdAccount}
            <Icon name="arrow-right" />
          </Button>
          <p
            className={styles.ctaNote}
            id="welcome-open-live-note"
            data-testid={testId('welcome', 'open-live', 'note')}
          >
            <Icon name="lock" size={14} />
            {WELCOME_COPY.openMyAdAccountNote}
          </p>
        </>
      )}
    </div>
  );
}

export function WelcomePage() {
  const [connecting, setConnecting] = useState(false);
  const { passwordGate, apiBuild } = useAppConfig();
  const navigate = useNavigate();
  return (
    <div className={styles.page} data-testid={testId('welcome', 'page')}>
      <div className={styles.offerBar}>
        <span>90 days to find your rhythm.</span>
        <strong>No credit card required.</strong>
        <Link to="/signup">
          Start free <Icon name="arrow-right" size={14} />
        </Link>
      </div>
      <header className={styles.top}>
        <Link to="/" className={styles.brand} data-testid={testId('welcome', 'brand')}>
          {/* The wordmark (D-1268) — the product's name is its accessible name. */}
          <BrandLogo height={28} />
        </Link>
        <div className={styles.topRight}>
          <button
            type="button"
            className={styles.navFeature}
            onClick={() => document.getElementById('features')?.scrollIntoView({ block: 'start' })}
          >
            Take a closer look
          </button>
          {passwordGate ? (
            <form action="/auth/login" method="post">
              <button
                type="submit"
                className={styles.loginButton}
                data-testid={testId('welcome', 'sign-in')}
              >
                Log in
              </button>
            </form>
          ) : (
            <Link to="/login" data-testid={testId('welcome', 'sign-in')}>
              Log in
            </Link>
          )}
          <Link to="/signup" className={styles.navTrial}>
            Start free trial <Icon name="arrow-right" size={14} />
          </Link>
        </div>
      </header>

      <main>
        <section className={styles.hero} aria-labelledby="welcome-title">
          <div className={styles.copy}>
            <span className={styles.overline}>
              For small businesses advertising on Facebook and Instagram
            </span>
            <h1
              id="welcome-title"
              className={styles.title}
              data-testid={testId('welcome', 'title')}
            >
              Less ad chaos.
              <br />
              <span className={styles.titleAccent}>More clarity.</span>
            </h1>
            <p className={styles.lead} data-testid={testId('welcome', 'lead')}>
              Your Facebook campaigns, finally in focus. {PRODUCT_NAME} brings{' '}
              <strong>spend, results and the next thing to watch</strong> into one beautifully
              simple workspace. Less digging. Better-informed decisions.
            </p>
            <div className={styles.cta}>
              <WelcomeActions />
              <ul className={styles.assure} data-testid={testId('welcome', 'assurances')}>
                <li>
                  <Icon name="check" size={14} />
                  90 days free
                </li>
                <li>
                  <Icon name="check" size={14} />
                  No credit card required
                </li>
                <li>
                  <Icon name="check" size={14} />
                  No automatic charges
                </li>
              </ul>
            </div>
            <div className={styles.later} data-testid={testId('welcome', 'connect')}>
              <span className={styles.laterIcon}>
                <Icon name="lock" />
              </span>
              <div>
                <Button
                  variant="secondary"
                  testID="welcome.connect.open"
                  onClick={() => {
                    if (apiBuild && !passwordGate) navigate('/account');
                    else setConnecting(true);
                  }}
                >
                  Connect your own ad account
                  <Icon name="arrow-right" size={14} />
                </Button>
                <p className={styles.laterText}>
                  Already running ads or starting fresh? We’ll guide you through it, step by step.
                </p>
              </div>
            </div>
          </div>

          <PerformancePreview testId="welcome.preview" />
        </section>

        <FeatureExplorer />

        <section
          className={styles.workflow}
          aria-labelledby="workflow-title"
          data-testid={testId('welcome', 'promises')}
        >
          <div className={styles.sectionIntro}>
            <span className={styles.overline}>A SIMPLE START</span>
            <h2 id="workflow-title">From inbox to insight.</h2>
            <p>One account. A quick email check. A clearer place for your campaigns.</p>
          </div>
          <ol className={styles.steps}>
            <li>
              <span>01</span>
              <h3>Create your account</h3>
              <p>
                Your name and email are all you need to begin. Your 90-day trial starts after
                verification. No credit card details to dig out.
              </p>
            </li>
            <li>
              <span>02</span>
              <h3>Confirm your email</h3>
              <p>
                Enter the one-time code from your inbox. It keeps your account secure, with no extra
                password to remember.
              </p>
            </li>
            <li>
              <span>03</span>
              <h3>Make yourself at home</h3>
              <p>
                Choose which Facebook accounts to connect, or explore a sample shop first. Your live
                campaigns stay view-only while you get comfortable.
              </p>
            </li>
          </ol>
        </section>

        <section className={styles.trialSection} aria-labelledby="trial-title">
          <div>
            <span className={styles.overline}>GIVE CLARITY A LITTLE TIME</span>
            <h2 id="trial-title">
              Your first 90 days.
              <br />
              On us.
            </h2>
            <p>Take the time to understand your campaigns and build a better daily routine.</p>
            <TrialPromise />
          </div>
          <div className={styles.trialCard}>
            <div className={styles.trialPrice}>
              $0<span>for 90 days</span>
            </div>
            <ul>
              <li>
                <Icon name="check" size={20} />
                Clear campaign and spend tracking
              </li>
              <li>
                <Icon name="check" size={20} />
                Performance comparisons and alerts
              </li>
              <li>
                <Icon name="check" size={20} />A workspace that works on your phone
              </li>
            </ul>
            <LinkButton to="/signup" variant="primary" size="lg">
              Start my free trial <Icon name="arrow-right" />
            </LinkButton>
            <strong>No credit card required.</strong>
            <p>Choose whether to upgrade later. No automatic billing.</p>
          </div>
        </section>

        <section className={styles.faq} aria-labelledby="faq-title">
          <h2 id="faq-title">A few things you might be wondering.</h2>
          <details>
            <summary>Do I need to enter a credit card?</summary>
            <p>
              No. You won’t be asked for payment details to start your 90-day trial, and we won’t
              automatically charge you when it ends. A paid subscription would require a separate,
              explicit purchase.
            </p>
          </details>
          <details>
            <summary>How do I create an account?</summary>
            <p>
              Enter your name and email, review and accept the terms, then confirm the code in your
              inbox. Returning users log in with an email code, too. There’s no password to
              remember.
            </p>
          </details>
          <details>
            <summary>Will this change my Facebook campaigns?</summary>
            <p>
              Connected Facebook accounts are currently view-only. You can track performance and
              plan your next steps without changing live ads. Editing and publishing in the
              interactive demo use sample data.
            </p>
          </details>
          <details>
            <summary>Can I look around first?</summary>
            <p>
              Absolutely. Choose “Try the demo” to explore a sample shop’s campaigns. No signup,
              Facebook connection or card is needed for the demo. The preview above shows
              illustrative sample data.
            </p>
          </details>
        </section>
        <section className={styles.lastCall} aria-labelledby="last-call-title">
          <span className={styles.overline}>A LITTLE LESS OVERWHELM STARTS HERE</span>
          <h2 id="last-call-title">
            Your ads are busy.
            <br />
            Your dashboard doesn’t have to be.
          </h2>
          <LinkButton to="/signup" variant="primary" size="lg">
            Give clarity a try <Icon name="arrow-right" />
          </LinkButton>
          <TrialPromise />
          <Link to="/signup?preview=1" className={styles.walkthroughLink}>
            Preview the signup experience
          </Link>
        </section>
      </main>

      <footer className={styles.bottom} data-testid={testId('welcome', 'footer')}>
        <span>
          {PRODUCT_NAME} · A service of Capital Market Laboratories, LLC.
          <br />
          Independent and not affiliated with Meta.
        </span>
        <nav aria-label="Legal">
          <Link to="/terms">Terms of service</Link>
          <Link to="/privacy">Privacy policy</Link>
        </nav>
      </footer>
      <OnboardingLauncher open={connecting} onClose={() => setConnecting(false)} />
    </div>
  );
}

/** The route component (app/router.tsx). */
export function WelcomeRoute() {
  usePageTitle('Welcome');
  return <WelcomePage />;
}
